import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Button, Dialog, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useDebounce } from 'hooks';
import { downloadEmailWithAttachments } from 'pages/common/downloadEml';
import PostalMime from 'postal-mime';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useFetchList,
  usePatchItem,
} from 'services/common/react-query-common-service';
import { getAllListData } from 'services/CommonServices';
import { MASTER_ENDPOINT, SALES_ENDPOINT } from 'utils/Constant';
import { formatPhoneNumber } from 'utils/Helpers';

import CandidateMatchScoresScreen from '../../candidate-match-scores';
import { JobAssignStatus, JobQueueTableType } from '../../enum/TeamUserRoles';
import CandidateMatches from './CandidateMatches';
import DownloadEMLModal from './DownloadEMLModal';
import JobBoardSearch from './JobBoardSearch';
import ShortlistedCandidates from './ShortlistedCandidates';
import SubcontractorOutreach, {
  type OutreachEmailContent,
  type OutreachPhase,
} from './SubcontractorOutreach';
import SubmitCandidatesPopup from './SubmitCandidatesPopup';
import SupportApproveCandidates from './SupportApproveCandidates';

interface RightSideTabProps {
  jobId?: string;
  jobInfo?: any;
  tableType?: string;
  refetch: any;
  score?: string;
  onScoreChange?: (score: string) => void;
}

const RightSideTab: React.FC<RightSideTabProps> = ({
  jobId,
  jobInfo,
  tableType,
  refetch,
  score,
  onScoreChange,
}) => {
  const isSupportPending = tableType === JobQueueTableType.SUPPORT_PENDING;
  const isRecruiter = [
    JobQueueTableType.RECRUITER_NEW,
    JobQueueTableType.RECRUITER_PENDING,
    JobQueueTableType.RECRUITER_SUBMITTED,
  ].includes(tableType as JobQueueTableType);
  const isNotAcceptedRecruiter =
    isRecruiter &&
    jobInfo?.jobAssignStatus !== JobAssignStatus.RECRUITER_ACCEPTED;
  const isSupport = [
    JobQueueTableType.SUPPORT_PENDING,
    JobQueueTableType.SUPPORT_SUBMITTED,
    JobQueueTableType.SUPPORT_TL_NEW,
    JobQueueTableType.SUPPORT_TL_ASSIGNED,
    JobQueueTableType.SUPPORT_TL_READY_FOR_REVIEW,
    JobQueueTableType.SUPPORT_TL_APPROVED,
  ].includes(tableType as JobQueueTableType);
  const isTeamLead = [
    JobQueueTableType.TEAM_LEAD_NEW,
    JobQueueTableType.TEAM_LEAD_ASSIGNED,
    JobQueueTableType.TEAM_LEAD_READY_FOR_REVIEW,
    JobQueueTableType.TEAM_LEAD_APPROVED,
  ].includes(tableType as JobQueueTableType);
  const isManagement = [
    JobQueueTableType.MANAGEMENT_JQ_UNALLOCATED,
    JobQueueTableType.MANAGEMENT_JQ_RECRUITER,
    JobQueueTableType.MANAGEMENT_JQ_TEAM_LEAD,
    JobQueueTableType.MANAGEMENT_JQ_SUPPORT,
    JobQueueTableType.MANAGEMENT_JQ_SUBMITTED,
    JobQueueTableType.MANAGEMENT_BQ_ALL_JOBS,
    JobQueueTableType.MANAGEMENT_BQ_MY_JOBS,
    JobQueueTableType.MANAGEMENT_BQ_OVERDUE,
    JobQueueTableType.MANAGEMENT_BQ_CLOSED,
  ].includes(tableType as JobQueueTableType);
  const isBusinessHead = [
    JobQueueTableType.BUSINESS_HEAD_UNALLOCATED,
    JobQueueTableType.BUSINESS_HEAD_ASSIGNED,
    JobQueueTableType.BUSINESS_HEAD_READY_FOR_REVIEW,
    JobQueueTableType.BUSINESS_HEAD_APPROVED,
    JobQueueTableType.BUSINESS_HEAD_SUBMITTED,
    JobQueueTableType.BH_BQ_ALL_JOBS,
    JobQueueTableType.BH_BQ_MY_JOBS,
    JobQueueTableType.BH_BQ_OVERDUE,
    JobQueueTableType.BH_BQ_CLOSED,
  ].includes(tableType as JobQueueTableType);

  const isManager = [
    JobQueueTableType.MANAGER_UNALLOCATED,
    JobQueueTableType.MANAGER_ASSIGNED,
    JobQueueTableType.MANAGER_READY_FOR_REVIEW,
    JobQueueTableType.MANAGER_APPROVED,
    JobQueueTableType.MANAGER_SUBMITTED,
  ].includes(tableType as JobQueueTableType);

  const showJobBoardTab =
    !!jobInfo?.allowJobBoardSearch &&
    (isRecruiter ||
      isSupport ||
      isTeamLead ||
      isManagement ||
      isBusinessHead ||
      isManager);
  const showSubcontractorsTab =
    !!jobInfo?.allowEmailCampaign &&
    (isRecruiter ||
      isSupport ||
      isTeamLead ||
      isManagement ||
      isBusinessHead ||
      isManager);

  // Persist outreach state across tab switches
  const [outreachPhase, setOutreachPhase] = useState<OutreachPhase>('idle');
  const [outreachEmailContent, setOutreachEmailContent] =
    useState<OutreachEmailContent | null>(null);

  // Persist Job Board state across tab switches
  const [jobBoardPhase, setJobBoardPhase] = useState<
    'idle' | 'boolean' | 'loading' | 'results'
  >(jobInfo?.hasTriggeredJobBoardSearch ? 'results' : 'idle');
  const [jobBoardResults, setJobBoardResults] = useState<any[]>([]);
  const [jobBoardBooleanData, setJobBoardBooleanData] = useState<any>(null);
  const [originalJobBoardBooleanData, setOriginalJobBoardBooleanData] =
    useState<any>(null);
  const [localMatchesScore, setLocalMatchesScore] = useState(score || '');
  const debouncedMatchesScore = useDebounce(localMatchesScore, 500);
  const [localShortlistedScore, setLocalShortlistedScore] = useState('0');
  const debouncedShortlistedScore = useDebounce(localShortlistedScore, 500);

  const isLocationSort =
    !jobInfo?.workType || jobInfo?.workType.toLowerCase() !== 'remote';
  const apiParams = new URLSearchParams({
    skip: '0',
    sourceQueue: 'jobQueue',
    take: '5',
    type: 'matched',
  });

  if (isLocationSort) {
    apiParams.append('sortby', 'location');
  }
  apiParams.append('score', debouncedMatchesScore || '0');

  const { data: matchedData, isLoading: isMatchesLoading } = useFetchList<any>(
    ['matched-candidates', jobId, debouncedMatchesScore, jobInfo?.workType],
    `${SALES_ENDPOINT.MatchedCandidates}/${jobId}?${apiParams.toString()}`,
    {
      enabled: !!jobId,
      refetchOnMount: 'always',
    },
  );

  // Fetch shortlisted candidates for the tab and modal
  const { data: allShortlistedData, isLoading: isLoadingShortlisted } =
    useFetchList<any>(
      ['all-shortlisted-candidates', jobId, debouncedShortlistedScore],
      `${
        SALES_ENDPOINT.MatchedCandidates
      }/${jobId}?type=shortlisted&sourceQueue=jobQueue&score=${String(
        debouncedShortlistedScore || '0',
      )}${isLocationSort ? '&sortby=location' : ''}`,
      {
        enabled: !!jobId,
        refetchOnMount: 'always',
      },
    );

  const tabs = isSupportPending
    ? [
        {
          id: 'Approved',
          label: "Approved Candidate's",
        },
      ]
    : [
        {
          id: 'Matches',
          label: `Matches ${
            (
              matchedData && matchedData.total !== undefined
                ? matchedData.total
                : jobInfo?.totalMatchedCandidates
            )
              ? `(${String(
                  matchedData && matchedData.total !== undefined
                    ? matchedData.total
                    : jobInfo?.totalMatchedCandidates,
                )})`
              : ''
          }`,
        },
        ...(showJobBoardTab
          ? [
              {
                id: 'Job Board',
                label: (
                  <span
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: '4px',
                    }}>
                    Job Board
                    <span
                      id="job-board-tab-icon-container"
                      style={{ display: 'flex' }}
                    />
                  </span>
                ),
              },
            ]
          : []),
        ...(showSubcontractorsTab
          ? [{ id: 'Subcontractors', label: 'Subcontractors' }]
          : []),
        {
          id: 'Shortlisted',
          label: `Shortlisted (${String(
            allShortlistedData && allShortlistedData.total !== undefined
              ? allShortlistedData.total
              : jobInfo?.totalCandidatesShortlisted || 0,
          )})`,
        },
      ];

  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const [selectedCandidates, setSelectedCandidates] = useState<
    Map<string, any>
  >(new Map());
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const [showEMLModal, setShowEMLModal] = useState<{
    open: boolean;
    type: 'download' | 'submit' | '';
  }>({
    open: false,
    type: '',
  });
  const [openScoreModal, setOpenScoreModal] = useState(false);
  const [openJobBoardModal, setOpenJobBoardModal] = useState(false);
  const [selectedScoreCandidateId, setSelectedScoreCandidateId] = useState<
    string | null
  >(null);
  const [localJobBoardScore, setLocalJobBoardScore] = useState('');
  const [hasJobBoardScoreBeenModified, setHasJobBoardScoreBeenModified] =
    useState(false);
  const debouncedJobBoardScore = useDebounce(localJobBoardScore, 500);

  const queryClient = useQueryClient();
  const { mutate: submitCandidates, isPending: isSubmitting } = usePatchItem();

  // Fetch all matched candidates for the modal
  const { data: allCandidatesData, isLoading: isLoadingCandidates } =
    useFetchList<any>(
      ['all-matched-candidates', jobId, debouncedMatchesScore],
      `${
        SALES_ENDPOINT.MatchedCandidates
      }/${jobId}?type=matched&sourceQueue=jobQueue&score=${String(
        debouncedMatchesScore || '0',
      )}${isLocationSort ? '&sortby=location' : ''}`,
      {
        enabled: !!jobId && openScoreModal,
        refetchOnMount: 'always',
      },
    );

  // Refetch internal matches ONLY when debounced matched score changes
  useEffect(() => {
    if (jobId && debouncedMatchesScore !== score) {
      refetch();
      queryClient.invalidateQueries({
        queryKey: ['matched-candidates', jobId],
      });
      queryClient.invalidateQueries({
        queryKey: ['shortlisted-candidates', jobId],
      });
      // Notify parent component of score change
      if (onScoreChange) {
        onScoreChange(debouncedMatchesScore);
      }
    }
  }, [
    debouncedMatchesScore,
    jobId,
    refetch,
    queryClient,
    score,
    onScoreChange,
  ]);

  const modalCandidates = useMemo(() => {
    return activeTab === 'Job Board' || openJobBoardModal
      ? jobBoardResults
      : [
          ...(allCandidatesData?.data || []),
          ...(allShortlistedData?.data || []),
        ];
  }, [
    activeTab,
    openJobBoardModal,
    jobBoardResults,
    allCandidatesData,
    allShortlistedData,
  ]);

  // Handler to open candidate scores screen
  const handleOpenCandidateModal = (candidateId?: string) => {
    setSelectedScoreCandidateId(candidateId || null);
    setOpenScoreModal(true);
  };

  // Helper functions for email generation
  const formatEmailField = (value?: string | string[] | null): string => {
    if (!value) {
      return '';
    }
    if (Array.isArray(value)) {
      return value
        .map((entry) => entry?.trim())
        .filter((entry): entry is string => Boolean(entry))
        .join(', ');
    }
    return value.trim();
  };

  const generateCandidateSignature = (candidate: any): string => {
    const c = candidate?.candidate;
    if (!c) return '';

    // Helper to escape HTML entities
    const escapeHtml = (text: any): string => {
      if (text === null || text === undefined || text === '') return '-';
      const str = String(text).trim();
      if (!str || str === '-') return '-';
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };

    // Helper to safely get field value
    const getField = (value: any, fallback = '-'): string => {
      if (value === null || value === undefined || value === '')
        return fallback;
      return String(value).trim() || fallback;
    };

    // Build name
    const firstName = getField(c.firstName, '');
    const middleName = getField(c.middleName, '');
    const lastName = getField(c.lastName, '');
    const fullNameRaw = [firstName, middleName, lastName]
      .filter(Boolean)
      .join(' ');
    const fullName = escapeHtml(fullNameRaw || '-');

    // Build location
    const city = getField(c.city, '');
    const state = getField(c.state, '');
    const country = getField(c.country, '');
    const locationParts = [city, state, country].filter(Boolean);
    const locationRaw =
      locationParts.length > 0 ? locationParts.join(', ') : '-';
    const location = escapeHtml(locationRaw);

    // Build contact (phone)
    const phone = c.phone || c.mobileNumber || c.workPhone || '';
    const formattedPhone = phone ? formatPhoneNumber(phone) : '-';
    const contact = escapeHtml(formattedPhone);

    // Build email
    const emailRaw = getField(c.email, '');
    const isValidEmail =
      emailRaw &&
      emailRaw !== '-' &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailRaw);
    const emailDisplay = escapeHtml(emailRaw);
    const emailLink = isValidEmail
      ? `<a href="mailto:${encodeURIComponent(
          emailRaw,
        )}" style="color: #0262b9; text-decoration: underline;">${emailDisplay}</a>`
      : emailDisplay;

    // Build visa status
    const visaStatusRaw = getField(c.workStatus?.name || c.workAuth, '-');
    const visaStatus = escapeHtml(visaStatusRaw);

    // Build LinkedIn
    const linkedinUrlRaw = c.linkedInUrl || c.linkedInUrl || '';
    let linkedin = '-';
    if (linkedinUrlRaw && linkedinUrlRaw.trim() && linkedinUrlRaw !== '-') {
      const linkedinUrl = getField(linkedinUrlRaw, '');
      let fullUrl = '';
      if (linkedinUrl.includes('http://') || linkedinUrl.includes('https://')) {
        fullUrl = linkedinUrl;
      } else {
        const profileId = encodeURIComponent(
          linkedinUrl.replace(/^\/+|\/+$/g, ''),
        );
        fullUrl = `https://www.linkedin.com/in/${profileId}`;
      }
      const escapedUrl = escapeHtml(linkedinUrl);
      const escapedFullUrl = fullUrl
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      linkedin = `<a href="${escapedFullUrl}" style="color: #0077b5; text-decoration: underline;">${escapedUrl}</a>`;
    }

    // Build experience
    const experience = c?.overallExperience || '-';

    // Build the signature HTML
    const signature = `
      <div style="margin-top: 20px; padding-top: 16px; font-family: Arial, sans-serif; font-size: 12px; color: #111827; line-height: 1.8;">
        <div style="font-weight: 600; font-size: 14px; color: #111827; margin-bottom: 12px;">${fullName}</div>
        <div style="color: #111827;">
          <div style="margin-bottom: 2px;"><strong>Location:</strong> ${location}</div>
          <div style="margin-bottom: 2px;"><strong>Experience:</strong> ${
            experience
              ? experience === 1
                ? experience + ' year'
                : experience + ' years'
              : '-'
          }</div>
          <div style="margin-bottom: 2px;"><strong>Contact:</strong> ${contact}</div>
          <div style="margin-bottom: 2px;"><strong>E-Mail:</strong> ${emailLink}</div>
          <div style="margin-bottom: 2px;"><strong>Visa Status:</strong> ${visaStatus}</div>
          <div style="margin-bottom: 2px;"><strong>LinkedIn:</strong> ${linkedin}</div>
        </div>
      </div>
    `;

    return signature;
  };

  const generateCandidateSignatures = (candidates: any[]): string => {
    if (!candidates || candidates.length === 0) return '';

    const signatures = candidates
      .map((candidate) => generateCandidateSignature(candidate))
      .filter(Boolean)
      .join('');

    return signatures;
  };

  const handleConfirmSubmission = () => {
    const candidateIds = Array.from(selectedCandidates.values()).map(
      (item) => item.candidate?.id,
    );
    submitCandidates(
      {
        data: {
          candidateId: candidateIds,
          isDownloadable: false,
          sourceQueue: 'jobQueue',
        },
        endpoint: `${SALES_ENDPOINT.MatchedCandidates}/submit-for-review/${jobId}`,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['shortlisted-candidates', jobId],
          });
          refetch();
          setShowSubmitPopup(false);
          setSelectedCandidates(new Map());
        },
      },
    );
  };

  // eslint-disable-next-line @typescript-eslint/require-await
  // eslint-disable-next-line @typescript-eslint/require-await
  const handleMarkAsSubmitted = async () => {
    const candidateIds = Array.from(selectedCandidates.values()).map(
      (item) => item.candidate?.id,
    );
    submitCandidates(
      {
        data: {
          candidateId: candidateIds,
          isDownloadable: true,
          sourceQueue: 'jobQueue',
        },
        endpoint: `${SALES_ENDPOINT.MatchedCandidates}/submit/${jobId}`,
      },
      {
        onSuccess: async (res: any) => {
          if (res?.zipUrl) {
            const getFileNameFromUrl = (urlString: string): string => {
              try {
                const { pathname } = new URL(urlString);
                const segment = pathname.substring(
                  pathname.lastIndexOf('/') + 1,
                );
                return decodeURIComponent(segment || 'attachment.zip');
              } catch (error) {
                console.warn(
                  'Unable to derive filename from URL',
                  urlString,
                  error,
                );
                return 'attachment.zip';
              }
            };

            if (showEMLModal.type === 'download') {
              const link = document.createElement('a');
              link.href = res.zipUrl;
              link.setAttribute('download', getFileNameFromUrl(res.zipUrl));
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } else {
              try {
                // Fetch email data directly
                let emailData = '';
                if (jobId && jobInfo?.jobSource === 'email') {
                  try {
                    const url = `${MASTER_ENDPOINT.EmlFiles}/${jobId}`;
                    const response = await getAllListData(url);

                    let emlText = '';
                    if (response instanceof Response) {
                      emlText = await response.text();
                    } else if (typeof response === 'string') {
                      emlText = response;
                    }

                    if (emlText) {
                      const parsed: any = await PostalMime.parse(emlText);
                      emailData = parsed?.html || parsed?.text || '';
                    }
                  } catch (error) {
                    // Handle error silently
                  }
                }

                const fromAddress = formatEmailField(jobInfo?.emailMeta?.from);
                const ccAddress = formatEmailField(jobInfo?.emailMeta?.cc);
                const subjectLine = formatEmailField(
                  jobInfo?.emailMeta?.subject,
                );

                // Prepend email body if available
                const emailBodyPrefix = emailData
                  ? `<div>${emailData}</div>\r\n`
                  : '';

                // Generate candidate signatures
                const selected = Array.from(selectedCandidates.values());
                const candidateSignatures =
                  generateCandidateSignatures(selected);

                // Get and escape job title
                const jobTitle = jobInfo?.jobTitle || 'role';
                const escapedJobTitle = jobTitle
                  .replace(/&/g, '&amp;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                  .replace(/"/g, '&quot;')
                  .replace(/'/g, '&#39;');

                const EMAIL_BODY_HTML = [
                  '<html><body style="font-family: Arial, sans-serif; font-size: 14px; color: #374151; line-height: 1.6;">',
                  '<p>Hello,</p>',
                  '<p>',
                  'Please find attached the list of shortlisted candidates who closely align ',
                  `with your requirements in terms of skills, experience, and project fit for your ${
                    escapedJobTitle ? String(escapedJobTitle) : ''
                  } role. `,
                  'Each profile has been reviewed to ensure relevance to the role and ',
                  'suitability for your current needs.',
                  '</p>',
                  '<p>',
                  'Kindly review the attached profiles and share your feedback or next steps ',
                  "at your convenience. We'll be happy to coordinate interviews or provide ",
                  'any additional information you may need.',
                  '</p>',
                  emailData
                    ? '<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 20px 0;">'
                    : '',
                  candidateSignatures
                    ? '<div style="margin-top: 24px;">' +
                      candidateSignatures +
                      '</div>'
                    : '',
                  emailData ? emailBodyPrefix : '',

                  '</body></html>',
                ].join('\r\n');
                const formatMessageId = (id: string) => `<${id}@itechindia.co>`;
                const fallbackFileName = getFileNameFromUrl(res?.zipUrl);
                await downloadEmailWithAttachments({
                  attachmentUrl: res?.zipUrl,
                  cc: ccAddress,
                  emailBodyHtml: EMAIL_BODY_HTML,
                  fallbackFileName,
                  from: jobInfo?.recruiterEmail
                    ? jobInfo?.recruiterEmail
                    : jobInfo?.emailMeta?.from,
                  jobData: jobInfo,
                  jobId: formatMessageId(
                    `reply-${String(jobInfo?.emailMeta?.jobId)}-${Date.now()}`,
                  ),
                  messageId: formatMessageId(jobInfo?.emailMeta?.messageId),
                  references: [formatMessageId(jobInfo?.emailMeta?.messageId)],
                  subject: subjectLine,
                  to: fromAddress,
                });
              } catch (error) {
                console.error('Failed to build EML with attachment', error);
              }
            }
          }
          queryClient.invalidateQueries({
            queryKey: ['support-approved-candidates', jobId],
          });
          setShowEMLModal({
            open: false,
            type: '',
          });
          setSelectedCandidates(new Map());
        },
      },
    );
  };

  const toggleSelection = (id: string, item: any) => {
    setSelectedCandidates((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, item);
      return next;
    });
  };

  const selectedIds = new Set(selectedCandidates.keys());

  const location = useLocation();

  const navigateTo = (job: any) => {
    // Determine the base path from current location
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const basePath =
      pathSegments.length > 0 ? `/${pathSegments[0]}` : '/business-head';

    let currentScore = debouncedMatchesScore;
    if (activeTab === 'Job Board') {
      currentScore = debouncedJobBoardScore;
    } else if (activeTab === 'Shortlisted') {
      currentScore = debouncedShortlistedScore;
    }

    navigate(`${basePath}/view-matched-candidate`, {
      state: {
        Params: {
          jobId: String(job.id ?? '1'),
          score: currentScore || '0',
          tableType,
          workType: jobInfo?.workType || null,
        },
      },
    });
  };

  return (
    <Box
      className="matches-section"
      sx={{
        position: 'relative',
        ...(isNotAcceptedRecruiter && {
          opacity: 0.6,
          pointerEvents: 'none',
          userSelect: 'none',
        }),
      }}>
      {isNotAcceptedRecruiter && (
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            left: 0,
            padding: '24px',
            pointerEvents: 'auto',
            position: 'absolute',
            right: 0,
            textAlign: 'center',
            top: 0,
            zIndex: 10,
          }}>
          <Typography
            sx={{
              color: '#1e293b',
              fontFamily: 'Inter',
              fontSize: '16px',
              fontWeight: 600,
              mb: 1,
            }}>
            Job Assignment Pending
          </Typography>
          <Typography
            sx={{
              color: '#64748b',
              fontFamily: 'Inter',
              fontSize: '13px',
              maxWidth: '280px',
            }}>
            Accept this job assignment to view matches and begin candidate
            sourcing.
          </Typography>
        </Box>
      )}
      <Box className="matches-tabs">
        <Box className="tabs-list">
          {tabs.map((tab) => (
            <Box
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => {
                if (tab.id === 'Job Board') {
                  setOpenJobBoardModal(true);
                } else {
                  setActiveTab(tab.id);
                }
              }}>
              {tab.label}
            </Box>
          ))}
        </Box>
        {(activeTab === 'Matches' || activeTab === 'Job Board') && (
          <Box
            className="view-scores-link"
            onClick={() => handleOpenCandidateModal()}>
            <span className="view-scores-text-full">View All Scores →</span>
            <span className="view-scores-text-short">Scores →</span>
          </Box>
        )}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '8px',
            marginLeft: 'auto',
          }}>
          {(activeTab === 'Matches' ||
            activeTab === 'Job Board' ||
            activeTab === 'Shortlisted') && (
            <Box className="percentage-input-container">
              <TextField
                key={activeTab}
                value={
                  activeTab === 'Job Board'
                    ? localJobBoardScore
                    : activeTab === 'Shortlisted'
                    ? localShortlistedScore
                    : localMatchesScore
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (activeTab === 'Job Board') {
                    setLocalJobBoardScore(val);
                    setHasJobBoardScoreBeenModified(true);
                  } else if (activeTab === 'Shortlisted') {
                    setLocalShortlistedScore(val);
                  } else {
                    setLocalMatchesScore(val);
                  }
                }}
                className="percentage-input"
                placeholder="0"
                size="small"
                type="text"
                autoComplete="off"
                spellCheck={false}
                InputProps={{
                  endAdornment: (
                    <Box
                      component="span"
                      sx={{
                        color: '#5E6C84',
                        fontSize: '13px',
                        pointerEvents: 'none',
                      }}>
                      %
                    </Box>
                  ),
                }}
              />
            </Box>
          )}
          {!isSupportPending && (
            <Box className="more-link" onClick={() => navigateTo(jobInfo)}>
              More <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Scrollable Content Area */}
      <Box className="matches-list">
        {activeTab === 'Approved' && (
          <SupportApproveCandidates
            jobId={jobId}
            score={debouncedMatchesScore}
            tableType={tableType}
            selectedIds={selectedIds}
            onToggleSelection={toggleSelection}
            onOpenCandidateModal={handleOpenCandidateModal}
          />
        )}
        {activeTab === 'Matches' && (
          <CandidateMatches
            score={debouncedMatchesScore}
            jobId={jobId}
            tableType={tableType}
            workType={jobInfo?.workType}
            data={matchedData}
            isLoading={isMatchesLoading}
            onOpenCandidateModal={handleOpenCandidateModal}
          />
        )}

        {/* Job Board is now a full-screen modal — nothing rendered inline */}
        {activeTab === 'Subcontractors' && (
          <Box className="matches-content-area">
            {isRecruiter ||
            isSupport ||
            isTeamLead ||
            isManagement ||
            isBusinessHead ||
            isManager ? (
              <SubcontractorOutreach
                jobId={jobId}
                jobInfo={jobInfo}
                phase={outreachPhase}
                emailContent={outreachEmailContent}
                onPhaseChange={setOutreachPhase}
                onEmailContentChange={setOutreachEmailContent}
              />
            ) : (
              <Box className="content-placeholder">
                <h3>Subcontractors</h3>
                <p>Subcontractor information will be displayed here.</p>
              </Box>
            )}
          </Box>
        )}
        {activeTab === 'Shortlisted' && (
          <ShortlistedCandidates
            score={debouncedShortlistedScore}
            jobId={jobId}
            tableType={tableType}
            selectedIds={selectedIds}
            onToggleSelection={toggleSelection}
            onOpenCandidateModal={handleOpenCandidateModal}
            data={allShortlistedData}
            isLoading={isLoadingShortlisted}
          />
        )}
      </Box>

      {/* Tab Bottom Footer */}
      {selectedCandidates.size > 0 &&
        (activeTab === 'Shortlisted' || activeTab === 'Approved') && (
          <Box className="selection-footer">
            {activeTab === 'Shortlisted' ? (
              <Button
                className="submit-btn"
                variant="contained"
                onClick={() => setShowSubmitPopup(true)}>
                Mark as Review ({selectedCandidates.size})
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: '10px' }}>
                <Button
                  className="download-btn"
                  variant="contained"
                  onClick={() =>
                    setShowEMLModal({
                      open: true,
                      type: 'download',
                    })
                  }>
                  Download docs
                </Button>
                <Button
                  className="submit-btn"
                  variant="contained"
                  onClick={() =>
                    setShowEMLModal({
                      open: true,
                      type: 'submit',
                    })
                  }>
                  Download EML
                </Button>
              </Box>
            )}
          </Box>
        )}

      {/* Download EML Confirmation Popup */}
      <DownloadEMLModal
        open={showEMLModal.open}
        onClose={() =>
          setShowEMLModal({
            open: false,
            type: '',
          })
        }
        onConfirm={handleMarkAsSubmitted}
        isSubmitting={isSubmitting}
      />

      {/* Submit Confirmation Popup */}
      <SubmitCandidatesPopup
        open={showSubmitPopup}
        onClose={() => setShowSubmitPopup(false)}
        selectedCandidates={selectedCandidates}
        isSubmitting={isSubmitting}
        onConfirm={handleConfirmSubmission}
        title="Review Candidates"
        subtitle="Review and confirm the candidates you want to mark for review."
        confirmLabel="Review Candidates"
      />

      {/* Score Modal */}
      <Dialog
        open={openScoreModal}
        onClose={() => setOpenScoreModal(false)}
        fullScreen>
        <CandidateMatchScoresScreen
          isModal={true}
          onClose={() => setOpenScoreModal(false)}
          candidates={modalCandidates}
          expFrom={jobInfo?.expFrom}
          expTo={jobInfo?.expTo}
          goal={jobInfo?.goal}
          jobTitle={jobInfo?.jobTitle}
          loading={
            activeTab === 'Job Board' || openJobBoardModal
              ? false
              : isLoadingCandidates || isLoadingShortlisted
          }
          selectedCandidateId={selectedScoreCandidateId || undefined}
        />
      </Dialog>

      {/* Job Board Full-Screen Modal */}
      <Dialog
        open={openJobBoardModal}
        onClose={() => setOpenJobBoardModal(false)}
        fullScreen>
        <JobBoardSearch
          jobId={jobId}
          jobInfo={jobInfo}
          phase={jobBoardPhase}
          results={jobBoardResults}
          booleanJsonData={jobBoardBooleanData}
          originalBooleanJsonData={originalJobBoardBooleanData}
          onPhaseChange={setJobBoardPhase}
          onResultsChange={setJobBoardResults}
          onBooleanJsonDataChange={setJobBoardBooleanData}
          onOriginalBooleanJsonDataChange={setOriginalJobBoardBooleanData}
          onOpenCandidateModal={handleOpenCandidateModal}
          score={debouncedJobBoardScore}
          isScoreModified={hasJobBoardScoreBeenModified}
          onClose={() => setOpenJobBoardModal(false)}
          localScore={localJobBoardScore}
          onScoreChange={(val) => {
            setLocalJobBoardScore(val);
            setHasJobBoardScoreBeenModified(true);
          }}
        />
      </Dialog>
    </Box>
  );
};

export default RightSideTab;
